// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Invoice from "@adl/XDL/Icons/Invoice";
import ProductLists from "@adl/XDL/Icons/ProductLists";
import OpenBids from "@adl/XDL/Icons/Quote";
import { color, xScreen } from "@adl/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { useGetLink, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps, ButtonVariants } from "@insite/mobius/Button";
import Icon from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { FC } from "react";
import React from "react";
import { css } from "styled-components";

const enum fields {
    itemsList = "itemsList",
    buttonLabel = "buttonLabel",
    buttonVariant = "buttonVariant",
    buttonLink = "buttonLink",
}

interface OwnProps {
    fields: {
        [fields.buttonLabel]: string;
        [fields.buttonVariant]: ButtonVariants;
        [fields.buttonLink]: LinkFieldValue;
        [fields.itemsList]: {
            fields: {
                title: string;
                imageUrl: string;
                linkTitle: string;
                link: LinkFieldValue;
                type: "productLists" | "openBids" | "openInvoices";
            };
        }[];
    };
}

type Props = OwnProps & WidgetProps;

interface UserQuickLinksStyles {
    wrapper?: InjectableCss;
    infoSection?: InjectableCss;
    titleSection?: InjectableCss;
    itemsSection?: InjectableCss;
    title?: TypographyPresentationProps;
    subtitle?: TypographyPresentationProps;
    button?: ButtonPresentationProps;
    blockContent?: InjectableCss;
    blockContentInfo?: InjectableCss;
    item?: {
        title?: TypographyPresentationProps;
        subtitle?: TypographyPresentationProps;
        image?: LazyImageProps;
        link?: LinkPresentationProps;
    };
}

const UserQuickLinksStyles: UserQuickLinksStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            ${xScreen("gt", "md")} {
                padding: 0 20px;
            }
        `,
    },
    infoSection: {
        css: css`
            display: flex;
            align-items: flex-start;
            justify-content: flex-start;
            flex-direction: column;
            min-width: 268px;
            margin-right: 60px;
            ${xScreen("lte", "md")} {
                width: 100%;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
                margin-right: 0;
            }
            ${xScreen("lte", "xs")} {
                min-width: initial;
                display: block;
            }
        `,
    },
    titleSection: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 24px;
            ${xScreen("lte", "1245")} {
                margin-bottom: 0px;
            }
            ${xScreen("lte", "xs")} {
                flex-direction: row;
                margin-bottom: 12px;
            }
        `,
    },
    title: {
        variant: "h4",
        css: css`
            margin-bottom: 4px;
            line-height: 25px;
            ${xScreen("lte", "xs")} {
                line-height: 25px;
                margin-right: 4px;
                margin-bottom: 0;
            }
        `,
    },
    subtitle: {
        variant: "h3",
        weight: "bold",
        css: css`
            line-height: 30px;
            text-transform: capitalize;
            margin-bottom: 0;
            ${xScreen("lte", "xs")} {
                font-size: 20px;
                line-height: 25px;
            }
        `,
    },
    itemsSection: {
        css: css`
            flex-grow: 1;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            flex-wrap: wrap;
            column-gap: 20px;
            & > div {
                flex-grow: 1;
            }
            ${xScreen("lte", "1245")} {
                margin-top: 32px;
                & > div:not(:last-child) {
                    margin-bottom: 20px;
                    margin-right: 15px;
                }
            }
            ${xScreen("lte", "xs")} {
                & > div {
                    width: 100%;
                    max-width: 100%;
                    &:not(:last-child) {
                        margin-right: 0;
                        margin-bottom: 30px;
                    }
                }
            }
        `,
    },
    blockContent: {
        css: css`
            width: 269px;
            border-top: 2px solid ${color("common.ui10", 0.5)};
            padding-top: 24px;
            display: flex;
            align-items: flex-start;
            ${xScreen("lte", "md")} {
                width: 230px;
                max-width: 230px;
            }
            ${({ hideOpenInvoicesLink }: any) => (hideOpenInvoicesLink ? "flex: 1 1 0px;max-width: 100%;" : "")}
        `,
    },
    blockContentInfo: {
        css: css`
            display: flex;
            align-items: flex-start;
            flex-direction: column;
        `,
    },
    item: {
        title: {
            variant: "h5",
            weight: "bold",
            css: css`
                margin-bottom: 8px;
            `,
        },
        subtitle: {
            size: 15,
            css: css`
                display: block;
                letter-spacing: 0;
                line-height: 24px;
                margin-bottom: 10px;
                color: ${color("common.ui7")};
                > ${TypographyStyle} {
                    color: ${color("text")};
                    margin-right: 8px;
                    line-height: 24px;
                }
            `,
        },
        link: {
            typographyProps: {
                size: 12,
                weight: 600,
                css: css`
                    letter-spacing: 0;
                    line-height: 18px;
                `,
            },
        },
        image: {
            css: css`
                flex-shrink: 0;
                margin-right: 22px;
                &,
                & img {
                    height: 68px;
                    width: 68px;
                }
            `,
        },
    },
};

const styles = UserQuickLinksStyles;

const defaultTitle = {
    productLists: "Product Lists",
    openBids: "Open Bids",
    openInvoices: "Open Invoices",
};

const iconsMap = {
    productLists: ProductLists,
    openBids: OpenBids,
    openInvoices: Invoice,
};

const UserQuickLinks: FC<Props> = ({ fields }) => {
    const { firstName, lastName, availableList, hideOpenInvoicesLink, isInvoiceDisabledUser } = useAppSelector(
        state => {
            const session = getSession(state);
            // XNG-Changes: Hide Open Invoices link from dashboard when the user has only `Buyer1` role
            const userRoles = session.isAuthenticated && !session.isGuest ? session.userRoles.trim().split(",") : [];
            const hideOpenInvoicesLink = userRoles.length === 1 && userRoles.includes("Buyer1");
            const userRolesUntrimmed = session.isAuthenticated ? session.userRoles : "";
            const isInvoiceDisabledUser = userRolesUntrimmed.includes("BuyerOnly");
            return {
                firstName: state.context.session.firstName,
                lastName: state.context.session.lastName,
                hideOpenInvoicesLink,
                isInvoiceDisabledUser,
                availableList: {
                    productLists: session.properties.numOfWishLists || 0,
                    openBids: session.properties.numOfOpenQuotes || 0,
                    openInvoices: session.properties.numOfOpenInvoices || 0,
                },
            };
        },
    );
    const buttonLink = useGetLink(fields.buttonLink);
    const itemsLinks = useGetLinks(fields.itemsList ?? [], o => o.fields.link);
    const history = useHistory();

    const onClick = (url?: string) => url && history.push(url);

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="UserQuickLinks">
            <StyledWrapper {...styles.infoSection}>
                <StyledWrapper {...styles.titleSection}>
                    <Typography {...styles.title} data-test-selector="UserQuickLinks_title">
                        {translate("Welcome")},
                    </Typography>
                    <Typography {...styles.subtitle} data-test-selector="UserQuickLinks_subtitle">
                        {`${firstName || ""} ${lastName || ""}`}
                    </Typography>
                </StyledWrapper>
                <Button {...styles.button} onClick={() => onClick(buttonLink?.url)} variant={fields.buttonVariant}>
                    {fields.buttonLabel}
                </Button>
            </StyledWrapper>
            {/* // XNG-Changes: fix links styling when the user has only `Buyer1` role */}
            <StyledWrapper {...styles.itemsSection}>
                {fields.itemsList?.map((itemField, index) => {
                    // XNG-Changes: Hide Open Invoices link from dashboard when the user has only `Buyer1` role
                    // also hide if the user has the role of 'InvoiceDisabled'
                    if ((hideOpenInvoicesLink || isInvoiceDisabledUser) && itemField.fields.type === "openInvoices") {
                        return null;
                    }

                    const item = {
                        title: itemField.fields.title || translate(defaultTitle[itemField.fields.type]),
                        numOfAvailable: availableList[itemField.fields.type] || 0,
                        linkTitle: itemField.fields.linkTitle,
                        imageUrl: itemField.fields.imageUrl,
                        link: itemsLinks?.[index].url || "",
                    };

                    return (
                        <StyledWrapper
                            {...styles.blockContent}
                            key={`${item.title}_${index}`}
                            {...{ hideOpenInvoicesLink }}
                        >
                            {item.imageUrl ? (
                                <LazyImage src={item.imageUrl} altText={item.title} {...styles.item?.image} />
                            ) : (
                                <Icon
                                    css={css`
                                        ${styles.item?.image?.css}
                                    `}
                                    color="common.ui20"
                                    size={68}
                                    src={iconsMap[itemField.fields.type]}
                                />
                            )}
                            <StyledWrapper>
                                <Typography {...styles.item?.title}>{item.title}</Typography>
                                <Typography {...styles.item?.subtitle}>
                                    <Typography weight="bold" size={20}>
                                        {item.numOfAvailable}
                                    </Typography>
                                    {translate("Available")}
                                </Typography>
                                <StyledWrapper>
                                    <Link href={item.link} {...styles.item?.link}>
                                        {item.linkTitle}
                                    </Link>
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: UserQuickLinks,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                fieldType: "General",
                defaultValue: "View My Dashboard",
            },
            {
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                fieldType: "General",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
            },
            {
                name: "itemsList",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                isVisible: item => !item.fields.isAuthenticated,
                fieldDefinitions: [
                    {
                        name: "type",
                        displayName: "Type",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Product Lists", value: "productLists" },
                            { displayName: "Open Bids", value: "openBids" },
                            { displayName: "Open Invoices", value: "openInvoices" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "productLists",
                    },
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "imageUrl",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "linkTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Url", value: "" },
                        allowCategories: item => false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
